<template>
  <div :class="$style.container">
    <PaymentsForm
      v-model="paymentDetails"
      :useMailingAddress="useMailingAddress"
    />
    <div :class="$style.buttonBar">
      <Button
        v-if="hasReachedSection"
        data-test-id="BillingInformationEdit__cancel"
        hasNoBackground
        text="Cancel"
        type="primary"
        @click="handleCancel"
      />
      <Button
        data-test-id="BillingInformationEdit__continue"
        hasOutline
        :isDisabled="isSubmitButtonDisabled"
        :isLoading="isPrimaryButtonLoading"
        text="Continue"
        type="secondary"
        @click="handleContinue"
      />
    </div>
  </div>
</template>

<script>
import {
  Button,
} from '@jumpcloud/ui-components';
import {
  PaymentsForm,
  paymentModuleName,
  responseMessages,
} from '@jumpcloud/ui-payments';
import {
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import {
  get,
} from 'lodash';
import {
  mapActions,
  mapGetters,
  mapState,
} from 'vuex';
import EventHelper from 'client/GrowthEngineering/EventHelper';

const {
  description,
  location,
  section,
  text: textEventProp,
} = eventProperties;

const orgModel = 'OrganizationModel';

export default {
  name: 'BillingInformationEdit',

  components: {
    Button,
    PaymentsForm,
  },

  EventHelper,

  props: {
    hasReachedSection: Boolean,
    location: {
      type: String,
      required: true,
    },
    orgId: {
      type: String,
      required: true,
    },
    section: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isPrimaryButtonLoading: false,
      useMailingAddress: false,
    };
  },

  computed: {
    ...mapState(paymentModuleName, {
      // The item isStored when it comes back from the server. Thus we are editing the exising item
      isInUpdateMode: 'isStored',
      item: 'item',
    }),

    ...mapGetters(paymentModuleName, [
      'areRequiredBillingFieldsSet',
      'areRequiredPaymentFieldsSet',
      'hasItemChanged',
      'isBillingEdited',
      'isMailingAndBillingEqual',
    ]),

    isSubmitButtonDisabled() {
      return !this.areRequiredBillingFieldsSet || !this.areRequiredPaymentFieldsSet;
    },

    paymentDetails: {
      get() {
        return this.item;
      },
      set(value) {
        this.updateItem({ payload: value.billing, type: 'billing' });
        this.updateItem({ payload: value.card, type: 'card' });
      },
    },
  },

  mounted() {
    this.initializePayments();
  },

  methods: {
    ...mapActions(paymentModuleName, [
      'resetItemToPrevious',
      'setIsProvider',
      'setIsStored',
      'setPreviousItem',
      'updateItem',
    ]),

    ...mapActions(orgModel, ['updatePaymentMailingDetails']),

    handleCancel() {
      if (this.isInUpdateMode && this.hasItemChanged) {
        this.resetItemToPrevious();
        this.useMailingAddress = this.isMailingAndBillingEqual;
      }
      this.$emit('cancelled');
    },

    async handleContinue() {
      if (this.isInUpdateMode && !this.hasItemChanged) {
        // They didn't make any changes and already have data saved, effectively just cancel
        this.handleCancel();
      } else {
        this.isPrimaryButtonLoading = true;
        this.$options.EventHelper.triggerButtonClicked({
          [description]: 'Continue clicked on Billing Information submission',
          [location]: this.location,
          [section]: this.section,
          [textEventProp]: 'Continue',
        });

        try {
          if (this.isInUpdateMode && !this.isBillingEdited) {
            await this.submitMailingAddressUpdate();
          } else {
            await this.storePaymentDetails();
          }
        } catch (err) {
          this.handleError(err);
        }

        this.isPrimaryButtonLoading = false;
      }
    },

    handleError(text = responseMessages.paymentSubmitError, err = '') {
      const errMsg = get(err, 'response.data.error', '') || get(err, 'message', '');
      const message = errMsg ? `${errMsg} ${text}` : text;

      this.$emit('error', message);
    },

    async initializePayments() {
      this.setIsProvider(false);

      if (this.isInUpdateMode) {
        this.useMailingAddress = this.isMailingAndBillingEqual;
      }
    },

    storePaymentDetails() {
      if (!this.isInUpdateMode) {
        this.setIsStored(true);
      }

      this.setPreviousItem(this.item);
      this.$emit('success', true);
    },

    async submitMailingAddressUpdate() {
      await this.updatePaymentMailingDetails({
        id: this.orgId,
        billingPayload: this.paymentDetails.billing,
      });

      this.setPreviousItem(this.item);
      this.$emit('success', false);
    },
  },
};
</script>

<style module scoped>
.buttonBar {
  display: flex;
  gap: var(--jc-spacer-large);
  justify-content: flex-end;
}

.container {
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
